// LoginEdit.js
import React, { useState } from 'react';

// Firebase
import { auth } from 'firebaseConfig';
import {
    reauthenticateWithCredential,
    EmailAuthProvider,
    updateEmail,
    updatePassword
} from 'firebase/auth';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// CSS
import './LoginEdit.css';

// Components
import Button from 'components/button/Button';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

/**
 * LoginEdit
 * 
 * Allows the user to securely update their login email or password.
 */
const LoginEdit = () => {

    // Global Context
    const { user } = useUser();

    // Theme Context
    const { theme } = useTheme();

    // State
    const [email, setEmail] = useState(user?.data_profile?.email || '');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState('');

    /**
     * Handles login update: re-authenticate, then update email/password
     */
    const handleSubmit = async () => {
        setStatus(null);

        if (!currentPassword) {
            setStatus('Please enter your current password to confirm.');
            return;
        }

        if (newPassword && newPassword !== confirmPassword) {
            setStatus('New passwords do not match.');
            return;
        }

        try {
            const authUser = auth.currentUser;

            // Re-authenticate
            const credential = EmailAuthProvider.credential(
                authUser.email,
                currentPassword
            );
            await reauthenticateWithCredential(authUser, credential);

            // Update email if changed
            if (email !== authUser.email) {
                await updateEmail(authUser, email);
            }

            // Update password if provided
            if (newPassword) {
                await updatePassword(authUser, newPassword);
            }

            setStatus('Login information updated successfully!');
        } catch (error) {
            console.error(error);
            setStatus(error.message || 'An error occurred while updating login info.');
        }
    };

    return (
        <div className="login-edit-container"
            style={{
                color: theme.foreground,
            }}>

            {/* EMAIL */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="Login Email" />
                <TextInput
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    width={350}
                />
            </div>

            {/* CURRENT PASSWORD */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="Current Password (required)" />
                <TextInput
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    width={350}
                />
            </div>

            {/* NEW PASSWORD */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="New Password (optional)" />
                <TextInput
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    width={350}
                />
            </div>

            {/* CONFIRM */}
            <div className="login-edit-field-wrapper">
                <ModalLabel label="Confirm New Password" />
                <TextInput
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    width={350}
                />
            </div>

            {status && <div className="login-edit-status">{status}</div>}

            {/* BUTTONS */}
            <div className="login-edit-buttons">
                <Button
                    iconKey="check"
                    label="Save Changes"
                    onClick={handleSubmit}
                />
            </div>

        </div>
    );
};

export default LoginEdit;
